import React, { useState, useRef, useEffect } from 'react';
import { useCurrency } from '../context/CurrencyContext';
import { 
  MessageSquare, 
  X, 
  Send, 
  Sparkles, 
  Loader2,
  Minimize2,
  Maximize2,
  Brain,
  TrendingUp,
  Package,
  Users,
  AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

/**
 * AI Chat Widget
 * Floating AI assistant that provides business insights
 * Connects to /api/ai endpoints
 */
export const AIChatWidget = () => {
  const { currencySymbol } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'assistant',
      content: 'مرحباً! أنا المساعد الذكي لتاجر برو. كيف يمكنني مساعدتك اليوم؟',
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Quick action suggestions
  const quickActions = [
    { icon: TrendingUp, label: 'توقعات المبيعات', action: 'sales-forecast' },
    { icon: Package, label: 'توصيات المخزون', action: 'inventory' },
    { icon: Users, label: 'تحليل العملاء', action: 'customers' },
    { icon: AlertTriangle, label: 'تنبيهات مهمة', action: 'alerts' },
  ];

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Build auth headers from individual localStorage keys (matches AuthContext storage)
  const getAuthHeaders = () => {
    const token     = localStorage.getItem('token');
    const companyId = localStorage.getItem('company_id');
    const userId    = localStorage.getItem('user_id');
    const headers   = { 'Content-Type': 'application/json' };
    if (token)     headers.Authorization    = `Bearer ${token}`;
    if (companyId) headers['X-Company-ID'] = companyId;
    if (userId)    headers['X-User-ID']    = userId;
    return headers;
  };

  const handleQuickAction = async (action) => {
    const actionMessages = {
      'sales-forecast': 'أريد معرفة توقعات المبيعات',
      'inventory': 'ما هي توصيات المخزون؟',
      'customers': 'أريد تحليل العملاء',
      'alerts': 'اعرض لي التنبيهات المهمة',
    };
    
    const userMessage = actionMessages[action];
    await sendMessage(userMessage, action);
  };

  const sendMessage = async (messageText, actionType = null) => {
    if (!messageText.trim() && !actionType) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      let response;
      let assistantContent = '';

      // Determine which API to call based on action or keywords
      if (actionType === 'sales-forecast' || messageText.includes('مبيعات') || messageText.includes('توقعات')) {
        response = await fetch(`${API_URL}/ai/sales-forecast`, {
          headers: getAuthHeaders(),
        });
        const data = await response.json();
        assistantContent = formatSalesForecast(data);
      } else if (actionType === 'inventory' || messageText.includes('مخزون') || messageText.includes('توصيات')) {
        response = await fetch(`${API_URL}/ai/inventory-recommendations`, {
          headers: getAuthHeaders(),
        });
        const data = await response.json();
        assistantContent = formatInventoryRecs(data);
      } else if (actionType === 'alerts' || messageText.includes('تنبيهات') || messageText.includes('تحذيرات')) {
        response = await fetch(`${API_URL}/ai/insights`, {
          headers: getAuthHeaders(),
        });
        const data = await response.json();
        const alerts = data.filter(i => i.priority === 'critical' || i.priority === 'high');
        assistantContent = formatAlerts(alerts);
      } else {
        // Default: get general insights
        response = await fetch(`${API_URL}/ai/insights`, {
          headers: getAuthHeaders(),
        });
        const data = await response.json();
        assistantContent = formatInsights(data);
      }

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: assistantContent,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: 'عذراً، حدث خطأ في جلب البيانات. يرجى المحاولة مرة أخرى.',
        isError: true,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Format helpers
  const formatSalesForecast = (data) => {
    if (!data) return 'لا تتوفر بيانات للتوقعات حالياً.';
    return `📊 **توقعات المبيعات**

📈 التوقع الشهري: ${data.monthly_forecast?.toLocaleString('ar-SA') || 'غير متوفر'} ${currencySymbol}
📅 الفترة: ${data.period || 'الشهر القادم'}
📉 معدل النمو المتوقع: ${data.growth_rate || 'غير محدد'}%

${data.recommendations ? `💡 التوصيات:\n${data.recommendations.join('\n')}` : ''}`;
  };

  const formatInventoryRecs = (data) => {
    if (!data || !data.recommendations?.length) return 'لا توجد توصيات للمخزون حالياً.';
    return `📦 **توصيات المخزون**

${data.recommendations.slice(0, 5).map((r, i) => 
  `${i + 1}. **${r.product_name || 'منتج'}**: ${r.recommendation || r.action || 'مراجعة الكمية'}`
).join('\n')}

${data.summary ? `\n📋 ${data.summary}` : ''}`;
  };

  const formatAlerts = (alerts) => {
    if (!alerts?.length) return '✅ لا توجد تنبيهات عاجلة حالياً. الوضع ممتاز!';
    return `⚠️ **التنبيهات المهمة**

${alerts.slice(0, 5).map((a, i) => 
  `${getPriorityEmoji(a.priority)} **${a.title || 'تنبيه'}**\n   ${a.message || a.description || ''}`
).join('\n\n')}`;
  };

  const formatInsights = (data) => {
    if (!data?.length) return 'لا تتوفر رؤى جديدة حالياً. يمكنك سؤالي عن المبيعات أو المخزون أو العملاء.';
    return `💡 **الرؤى والتوصيات**

${data.slice(0, 4).map((i, idx) => 
  `${idx + 1}. ${i.title || 'رؤية'}: ${i.message || i.description || ''}`
).join('\n\n')}

اسألني عن المزيد من التفاصيل!`;
  };

  const getPriorityEmoji = (priority) => {
    switch (priority) {
      case 'critical': return '🔴';
      case 'high': return '🟠';
      case 'medium': return '🟡';
      default: return '🟢';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Floating button when closed
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-gradient-to-br from-primary to-primary-dark rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center group"
        aria-label="فتح المساعد الذكي"
      >
        <Brain className="w-7 h-7 text-white" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
        
        {/* Tooltip */}
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-slate-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          المساعد الذكي
        </span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 left-6 z-50 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 transition-all overflow-hidden ${
        isMinimized ? 'w-72 h-14' : 'w-96 h-[500px]'
      }`}
      dir="rtl"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-l from-primary to-primary-dark text-white">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm">المساعد الذكي</h3>
            {!isMinimized && (
              <p className="text-xs text-white/70">متصل ونشط</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            aria-label={isMinimized ? 'توسيع' : 'تصغير'}
          >
            {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="إغلاق"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="h-[340px] overflow-y-auto p-4 space-y-4 bg-slate-50 dark:bg-slate-900">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.type === 'user' ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    msg.type === 'user'
                      ? 'bg-primary text-white rounded-tr-none'
                      : msg.isError
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-tl-none'
                        : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 shadow-sm rounded-tl-none'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">
                    {msg.content}
                  </p>
                  <p className={`text-xs mt-1 ${
                    msg.type === 'user' ? 'text-white/60' : 'text-slate-400'
                  }`}>
                    {new Date(msg.timestamp).toLocaleTimeString('ar-SA', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-end">
                <div className="bg-white dark:bg-slate-800 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2 text-primary">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">جاري التحليل...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length <= 2 && (
            <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
              <p className="text-xs text-slate-500 mb-2">اقتراحات سريعة:</p>
              <div className="flex flex-wrap gap-1.5">
                {quickActions.map((action) => (
                  <button
                    key={action.action}
                    onClick={() => handleQuickAction(action.action)}
                    className="flex items-center gap-1 px-2 py-1 bg-slate-100 dark:bg-slate-700 hover:bg-primary/10 dark:hover:bg-primary/20 text-slate-700 dark:text-slate-300 rounded-full text-xs transition-colors"
                    disabled={isLoading}
                  >
                    <action.icon size={12} />
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form 
            onSubmit={handleSubmit}
            className="p-3 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
          >
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="اسأل عن المبيعات، المخزون، العملاء..."
                className="flex-1 px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-xl text-sm border-0 focus:ring-2 focus:ring-primary/50 dark:text-white"
                disabled={isLoading}
              />
              <Button
                type="submit"
                size="icon"
                disabled={!input.trim() || isLoading}
                className="rounded-xl"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default AIChatWidget;
